import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import getBookCoverUrl from '../utils/getBookCoverUrl';
import '../styles/DashBoard.css';

const BookPreview = ({ book, onSelect }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    const rect = e.target.getBoundingClientRect();
    const isButton = e.clientY > (rect.top + rect.height - 40);

    if (isButton) {
      navigate(`/book/${book.book_id}`);
    } else {
      onSelect(book);
    }
  };

  return (
    <div className="book-preview" onClick={handleClick}>
      <img
        src={getBookCoverUrl(book.cover_image_url)}
        alt={book.title}
        onError={(e) => { e.target.onerror = null; e.target.src = getBookCoverUrl(); }}
      />
      <div className="preview-overlay">
        <button className="preview-button">Önizle</button>
      </div>
    </div>
  );
};

const BookModal = ({ book, onClose }) => {
  const navigate = useNavigate();
  const [hasReadingProgress, setHasReadingProgress] = useState(false);
  const [lastChapterId, setLastChapterId] = useState(null);
  const [isProgressLoading, setIsProgressLoading] = useState(true);

  useEffect(() => {
    checkReadingProgress();
  }, [book.book_id]);

  // Okuma ilerlemesini kontrol eden fonksiyon
  const checkReadingProgress = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsProgressLoading(false);
      return;
    }

    try {
      setIsProgressLoading(true);
      const response = await api.get(`/bookReader/progress/${book.book_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const { last_chapter_id } = response.data;
      if (last_chapter_id) {
        setHasReadingProgress(true);
        setLastChapterId(last_chapter_id);
      } else {
        setHasReadingProgress(false);
        setLastChapterId(null);
      }
    } catch (error) {
      // Okuma ilerlemesi yoksa normal durum
      setHasReadingProgress(false);
      setLastChapterId(null);
    } finally {
      setIsProgressLoading(false);
    }
  };

  // Progress kaydetme fonksiyonu
  const saveInitialProgress = async (chapterId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const position = { scrollY: 0, timestamp: Date.now() };

      await api.post(`/bookReader/progress/save`, {
        bookId: parseInt(book.book_id),
        chapterId: parseInt(chapterId),
        position: position,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('İlk okuma ilerlemesi kaydedildi');
    } catch (error) {
      console.error('İlk okuma ilerlemesi kaydedilirken hata oluştu:', error);
    }
  };

  const handleReadClick = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error("Kullanıcı kimlik doğrulaması yapılmadı. Token bulunamadı.");
      return;
    }

    // Eğer okuma ilerlemesi varsa, kaldığı yerden devam et
    if (hasReadingProgress && lastChapterId) {
      navigate(`/read/${book.book_id}/${lastChapterId}`);
      return;
    }

    // Okuma ilerlemesi yoksa, ilk bölümden başla
    try {
      const response = await api.get(`/books/${book.book_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const chapters = response.data.chapters;

      if (chapters && chapters.length > 0) {
        const firstChapter = chapters.find(ch => ch.chapter_number === 1);
        const firstChapterId = firstChapter ? firstChapter.chapter_id : chapters[0].chapter_id;
        
        // İlk progress kaydını yap
        await saveInitialProgress(firstChapterId);
        
        navigate(`/read/${book.book_id}/${firstChapterId}`);
      } else {
        console.error("Kitabın bölümleri bulunamadı.");
      }
    } catch (error) {
      console.error("Bölümler yüklenirken hata oluştu:", error);
    }
  };

  const handleMoreInfoClick = () => {
    navigate(`/book/${book.book_id}`);
  };

  return (
    <div className="book-modal-overlay">
      <div className="book-modal">
        <button className="close-button" onClick={onClose}>×</button>
        <div className="book-modal-content">
          <img
            src={getBookCoverUrl(book.cover_image_url)}
            alt={book.title}
            onError={(e) => { e.target.onerror = null; e.target.src = getBookCoverUrl(); }}
          />
          <div className="book-details">
            <h2>{book.title}</h2>
            <div className="book-meta">
              <span>{book.total_views} görüntülenme</span>
              <span>{book.total_likes} beğeni</span>
            </div>
            <div className="actions">
              <button 
                className="read-button" 
                onClick={handleReadClick}
                disabled={isProgressLoading}
              >
                {isProgressLoading ? '...' : (hasReadingProgress ? 'Devam et' : 'Okumaya Başla')}
              </button>
              <button className="more-info-button" onClick={handleMoreInfoClick}>Daha Fazla Bilgi →</button>
            </div>
            <p>{book.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookShelf = ({ title, books }) => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - e.currentTarget.offsetLeft);
    setScrollLeft(e.currentTarget.scrollLeft);
    e.currentTarget.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - e.currentTarget.offsetLeft;
    const walk = (x - startX) * 2;
    e.currentTarget.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
  };

  const handleMouseLeave = (e) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
  };

  // Drag sırasında kitap tıklamalarını engelle
  const handleBookSelect = (book) => {
    if (!isDragging) {
      setSelectedBook(book);
    }
  };

  return (
    <div className="book-shelf">
      <h2>{title}</h2>
      <div 
        className="book-list"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ userSelect: 'none' }}
      >
        {books.length === 0 ? (
          title === "Kütüphaneniz" ? (
            <p className="empty-library-message">Kütüphanenizde henüz kitap yok.</p>
          ) : (
            <p className="empty-shelf-message">Bu kategoride kitap yok.</p>
          )
        ) : (
          books.map((book) => (
            <BookPreview key={book.book_id} book={book} onSelect={handleBookSelect} />
          ))
        )}
      </div>
      {selectedBook && (
        <BookModal
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
        />
      )}
    </div>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    yourLibrary: [],
    recommended: [],
    newReleases: [],
    popular: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await api.get('/dashboard/dashboard', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const enrichedData = {
          ...response.data,
          yourLibrary: response.data.yourLibrary.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          recommended: response.data.recommended.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          newReleases: response.data.newReleases.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
          popular: response.data.popular.map(book => ({
            ...book,
            cover_image_url: getBookCoverUrl(book.cover_image_url)
          })),
        };

        setDashboardData(enrichedData);
        setLoading(false);
      } catch (error) {
        console.error('Dashboard verileri alınırken hata oluştu:', error.response?.data ?? error.message);
        setError(`Dashboard verileri alınırken hata oluştu: ${error.response?.data ?? error.message}`);
        setLoading(false);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      }
    };

    fetchDashboardData();
  }, [navigate]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="book-dashboard">
      <h1>Yeni Favori Hikayeni Bul</h1>
      <BookShelf title="Kütüphaneniz" books={dashboardData.yourLibrary} />
      <BookShelf title="Sizin İçin Önerilen" books={dashboardData.recommended.slice(0,10)} />
      <BookShelf title="Yeni Çıkanlar" books={dashboardData.newReleases.slice(0,10)} />
      <BookShelf title="Popüler Kitaplar" books={dashboardData.popular.slice(0,10)} />
    </div>
  );
};

export default Dashboard;
